import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { useLoading } from "../contexts/LoadingContext";
import { Icon, IconName } from "./Icon";

// ponytail: shared inline style so guide headings align their icon to the text
const gi: React.CSSProperties = { verticalAlign: "-3px", marginRight: "8px" };
const GuideIcon = ({ name }: { name: IconName }) => (
  <Icon name={name} size={16} style={gi} />
);

interface ConversationMetrics {
  totalMessages: number;
  uniqueUsers: number;
  messagesPerHour: Record<number, number>;
  peakHour: { hour: number; count: number };
  averageMessageLength: number;
  sentimentBreakdown: {
    positive: number;
    negative: number;
    neutral: number;
    questions: number;
  };
  topUsers: Array<{
    username: string;
    messageCount: number;
    avgMessageLength: number;
    sentiment: string;
  }>;
  conversationThreads: number;
  topicDistribution: Record<string, number>;
}

interface TopicTrend {
  topic: string;
  mentions: number;
  trend: "rising" | "stable" | "declining";
  percentChange: number;
  relatedKeywords: string[];
}

interface PeakTimes {
  hourly: Record<number, number>;
  daily: Record<string, number>;
  peakHour: { hour: number; avgMessages: number };
  peakDay: { day: string; avgMessages: number };
}

interface StreamAnalytics {
  timeRange: "24h" | "7d" | "30d";
  viewerData: Array<{
    timestamp: Date;
    maxViewers: number;
    label: string;
  }>;
  streams: Array<{
    id: string;
    title: string;
    category: string;
    startedAt: Date;
    endedAt: Date | null;
    duration: number;
    peakViewers: number;
    avgViewers: number;
  }>;
  totalStreams: number;
  totalStreamTime: number;
  avgStreamDuration: number;
  overallPeakViewers: number;
  avgPeakViewers: number;
}

interface AnalyticsDashboardProps {
  authToken: string;
  userType?: "user" | "premium" | "vip" | "admin";
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  authToken,
  userType = "user",
}) => {
  const [metrics, setMetrics] = useState<ConversationMetrics | null>(null);
  const [topicTrends, setTopicTrends] = useState<TopicTrend[]>([]);
  const [_peakTimes, setPeakTimes] = useState<PeakTimes | null>(null);
  const [streamAnalytics, setStreamAnalytics] =
    useState<StreamAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [_selectedUser, _setSelectedUser] = useState<string>("");
  const [isMobile, setIsMobile] = useState(false);
  const [showTooltip7d, setShowTooltip7d] = useState(false);
  const [showTooltip30d, setShowTooltip30d] = useState(false);
  const location = useLocation();
  const { setAnalyticsLoading } = useLoading();

  // Determine active tab from route
  const getActiveTab = (): "chat" | "stream" => {
    if (location.pathname.includes("/stream/")) return "stream";
    return "chat";
  };

  const activeTab = getActiveTab();

  // Determine time range from route
  const getTimeRange = (): "24h" | "7d" | "30d" => {
    if (location.pathname.includes("last7days")) return "7d";
    if (location.pathname.includes("last30days")) return "30d";
    return "24h"; // default to last 24 hours
  };

  const timeRange = getTimeRange();

  useEffect(() => {
    // Check if free user is trying to access premium time ranges
    if (userType === "user" && (timeRange === "7d" || timeRange === "30d")) {
      // Redirect to 24h view
      window.location.href = `/dashboard/analytics/${activeTab}/last24hours`;
      return;
    }
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, activeTab]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setAnalyticsLoading(true);
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();

      if (timeRange === "24h") {
        startDate.setDate(startDate.getDate() - 1);
      } else if (timeRange === "7d") {
        startDate.setDate(startDate.getDate() - 7);
      } else {
        startDate.setDate(startDate.getDate() - 30);
      }

      // Fetch all analytics data in parallel
      const [metricsRes, trendsRes, peakRes] = await Promise.all([
        axios.get("/api/bot/analytics/conversation-flow", {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        axios.get("/api/bot/analytics/topic-trends", {
          params: {
            days: timeRange === "24h" ? 1 : timeRange === "7d" ? 7 : 30,
          },
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        axios.get("/api/bot/analytics/peak-times", {
          params: {
            days: timeRange === "24h" ? 1 : timeRange === "7d" ? 7 : 30,
          },
          headers: { Authorization: `Bearer ${authToken}` },
        }),
      ]);

      setMetrics(metricsRes.data);
      setTopicTrends(trendsRes.data);
      setPeakTimes(peakRes.data);

      // Fetch stream analytics if on stream tab
      if (activeTab === "stream") {
        const streamRes = await axios.get("/api/bot/analytics/streams", {
          params: { timeRange },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setStreamAnalytics(streamRes.data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
      setAnalyticsLoading(false);
    }
  };

  const getTrendIcon = (trend: "rising" | "stable" | "declining") => {
    const name: IconName =
      trend === "rising"
        ? "trending-up"
        : trend === "declining"
          ? "trending-down"
          : "arrow-right";
    return <Icon name={name} size={15} style={{ verticalAlign: "-2px" }} />;
  };

  const formatHour = (hour: number) => {
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}${ampm}`;
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return "#10B981";
      case "negative":
        return "#EF4444";
      case "question":
        return "#3B82F6";
      default:
        return "#6B7280";
    }
  };

  // Create hourly activity chart data
  const getHourlyChartBars = () => {
    if (!metrics) return [];

    const maxCount = Math.max(...Object.values(metrics.messagesPerHour));
    const bars = [];

    for (let hour = 0; hour < 24; hour++) {
      const count = metrics.messagesPerHour[hour] || 0;
      const height = maxCount > 0 ? (count / maxCount) * 100 : 0;

      bars.push(
        <div
          key={hour}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100px",
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <div
              style={{
                width: "100%",
                height: `${height}%`,
                backgroundColor:
                  hour === metrics.peakHour.hour ? "#9146FF" : "#E0E7FF",
                borderRadius: "4px 4px 0 0",
                transition: "height 0.3s ease",
              }}
              title={`${formatHour(hour)}: ${count} messages`}
            />
          </div>
          <span style={{ fontSize: "10px", color: "#cbd5e0" }}>
            {hour % 6 === 0 ? formatHour(hour) : ""}
          </span>
        </div>,
      );
    }

    return bars;
  };

  const getViewerChartBars = () => {
    if (!streamAnalytics || !streamAnalytics.viewerData) return [];

    const maxViewers = Math.max(
      ...streamAnalytics.viewerData.map((d) => d.maxViewers),
      1,
    );

    return streamAnalytics.viewerData.map((data, index) => (
      <div
        key={index}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "150px",
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              width: "100%",
              height: `${(data.maxViewers / maxViewers) * 100}%`,
              backgroundColor: "#9146FF",
              borderRadius: "4px 4px 0 0",
              transition: "height 0.3s ease",
              minHeight: data.maxViewers > 0 ? "2px" : "0",
            }}
            title={`${data.label}: ${data.maxViewers} viewers`}
          />
        </div>
        <span
          style={{ fontSize: "10px", color: "#cbd5e0", textAlign: "center" }}
        >
          {streamAnalytics.timeRange === "24h" || index % 5 === 0
            ? data.label
            : ""}
        </span>
      </div>
    ));
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Remove loading screen to prevent flash

  const AnalyticsContent = () => (
    <>
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.05);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          gap: "20px",
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "0 20px",
        }}
      >
        <div
          style={{
            flex: "1",
            maxWidth: isMobile ? "100%" : "800px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? "16px" : "0",
              marginBottom: "24px",
            }}
          >
            <h3 style={{ fontSize: "20px", margin: 0, color: "#e2e8f0" }}>
              Analytics Dashboard
            </h3>

            {/* Time Range Selector */}
            <div style={{ display: "flex", gap: "8px" }}>
              <Link
                to={`/dashboard/analytics/${activeTab}/last24hours`}
                style={{
                  padding: "6px 16px",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: timeRange === "24h" ? "#9146FF" : "#2d3748",
                  color: timeRange === "24h" ? "white" : "#cbd5e0",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: timeRange === "24h" ? "bold" : "normal",
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                Last 24 Hours
              </Link>
              {userType === "premium" ||
              userType === "vip" ||
              userType === "admin" ? (
                <Link
                  to={`/dashboard/analytics/${activeTab}/last7days`}
                  style={{
                    padding: "6px 16px",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: timeRange === "7d" ? "#9146FF" : "#2d3748",
                    color: timeRange === "7d" ? "white" : "#cbd5e0",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: timeRange === "7d" ? "bold" : "normal",
                    textDecoration: "none",
                    display: "inline-block",
                  }}
                >
                  Last 7 Days
                </Link>
              ) : (
                <div
                  style={{
                    padding: "6px 16px",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#2d3748",
                    color: "#718096",
                    cursor: "not-allowed",
                    fontSize: "14px",
                    fontWeight: "normal",
                    display: "inline-block",
                    position: "relative",
                  }}
                  onMouseEnter={() => setShowTooltip7d(true)}
                  onMouseLeave={() => setShowTooltip7d(false)}
                >
                  <Icon
                    name="lock"
                    size={13}
                    style={{ verticalAlign: "-2px", marginRight: "4px" }}
                  />
                  Last 7 Days
                  {showTooltip7d && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        marginTop: "8px",
                        padding: "8px 12px",
                        backgroundColor: "rgba(26, 32, 44, 0.95)",
                        border: "1px solid #9146FF",
                        borderRadius: "6px",
                        fontSize: "12px",
                        color: "#ffffff",
                        whiteSpace: "nowrap",
                        zIndex: 9999,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      Premium feature - 7 day analytics
                    </div>
                  )}
                </div>
              )}
              {userType === "premium" ||
              userType === "vip" ||
              userType === "admin" ? (
                <Link
                  to={`/dashboard/analytics/${activeTab}/last30days`}
                  style={{
                    padding: "6px 16px",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor:
                      timeRange === "30d" ? "#9146FF" : "#2d3748",
                    color: timeRange === "30d" ? "white" : "#cbd5e0",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: timeRange === "30d" ? "bold" : "normal",
                    textDecoration: "none",
                    display: "inline-block",
                  }}
                >
                  Last 30 Days
                </Link>
              ) : (
                <div
                  style={{
                    padding: "6px 16px",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#2d3748",
                    color: "#718096",
                    cursor: "not-allowed",
                    fontSize: "14px",
                    fontWeight: "normal",
                    display: "inline-block",
                    position: "relative",
                  }}
                  onMouseEnter={() => setShowTooltip30d(true)}
                  onMouseLeave={() => setShowTooltip30d(false)}
                >
                  <Icon
                    name="lock"
                    size={13}
                    style={{ verticalAlign: "-2px", marginRight: "4px" }}
                  />
                  Last 30 Days
                  {showTooltip30d && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        marginTop: "8px",
                        padding: "8px 12px",
                        backgroundColor: "rgba(26, 32, 44, 0.95)",
                        border: "1px solid #9146FF",
                        borderRadius: "6px",
                        fontSize: "12px",
                        color: "#ffffff",
                        whiteSpace: "nowrap",
                        zIndex: 9999,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      Premium feature - 30 day analytics
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div
            style={{
              display: "flex",
              gap: "2px",
              borderBottom: "1px solid #4a5568",
              marginBottom: "20px",
            }}
          >
            <Link
              to={`/dashboard/analytics/chat/${timeRange === "24h" ? "last24hours" : timeRange === "7d" ? "last7days" : "last30days"}`}
              style={{
                backgroundColor:
                  activeTab === "chat" ? "#9146FF" : "transparent",
                color: activeTab === "chat" ? "white" : "#a0aec0",
                border: "none",
                padding: "12px 24px",
                borderRadius: "6px 6px 0 0",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.2s ease",
                textDecoration: "none",
                display: "inline-block",
              }}
            >
              Chat Analytics
            </Link>
            <Link
              to={`/dashboard/analytics/stream/${timeRange === "24h" ? "last24hours" : timeRange === "7d" ? "last7days" : "last30days"}`}
              style={{
                backgroundColor:
                  activeTab === "stream" ? "#9146FF" : "transparent",
                color: activeTab === "stream" ? "white" : "#a0aec0",
                border: "none",
                padding: "12px 24px",
                borderRadius: "6px 6px 0 0",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.2s ease",
                textDecoration: "none",
                display: "inline-block",
              }}
            >
              Stream Analytics
            </Link>
          </div>

          {activeTab === "chat" ? (
            <>
              {loading ? (
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: isMobile ? "40px 20px" : "60px",
                    borderRadius: "8px",
                    textAlign: "center",
                    marginTop: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "400px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "48px",
                      marginBottom: "16px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  >
                    <Icon name="bar-chart-3" size={40} color="#9146FF" />
                  </div>
                  <h3
                    style={{
                      color: "#e2e8f0",
                      marginTop: 0,
                      marginBottom: "12px",
                      fontSize: "20px",
                    }}
                  >
                    Loading Analytics...
                  </h3>
                  <p
                    style={{
                      color: "#a0aec0",
                      fontSize: "16px",
                    }}
                  >
                    Fetching your chat data
                  </p>
                </div>
              ) : metrics && metrics.totalMessages > 0 ? (
                <div style={{ display: "grid", gap: "20px" }}>
                  {/* Key Metrics */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "repeat(2, 1fr)"
                        : "repeat(4, 1fr)",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#9146FF",
                        }}
                      >
                        {metrics.totalMessages}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Total Messages
                      </div>
                    </div>
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#9146FF",
                        }}
                      >
                        {metrics.uniqueUsers}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Active Users
                      </div>
                    </div>
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#9146FF",
                        }}
                      >
                        {metrics.conversationThreads}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Conversations
                      </div>
                    </div>
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#9146FF",
                        }}
                      >
                        {metrics.averageMessageLength}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Avg Message Length
                      </div>
                    </div>
                  </div>

                  {/* Hourly Activity Chart */}
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "20px",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                      width: "100%",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        color: "#e2e8f0",
                      }}
                    >
                      Activity by Hour
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        gap: "2px",
                        alignItems: "flex-end",
                      }}
                    >
                      {getHourlyChartBars()}
                    </div>
                    {metrics.peakHour && (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "12px",
                          fontSize: "14px",
                          color: "#cbd5e0",
                        }}
                      >
                        Peak activity at {formatHour(metrics.peakHour.hour)}{" "}
                        with {metrics.peakHour.count} messages
                      </p>
                    )}
                  </div>

                  {/* Two Column Layout */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    {/* Top Users */}
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                      }}
                    >
                      <h4
                        style={{
                          marginTop: 0,
                          marginBottom: "16px",
                          color: "#e2e8f0",
                        }}
                      >
                        Most Active Users
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {metrics.topUsers.slice(0, 5).map((user, idx) => (
                          <div
                            key={user.username}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px",
                              backgroundColor: "#374151",
                              borderRadius: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                              }}
                            >
                              <span
                                style={{ fontSize: "18px", color: "#cbd5e0" }}
                              >
                                #{idx + 1}
                              </span>
                              <span
                                style={{ fontWeight: "500", color: "#e2e8f0" }}
                              >
                                {user.username}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "12px",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{ fontSize: "14px", color: "#cbd5e0" }}
                              >
                                {user.messageCount} msgs
                              </span>
                              <span
                                style={{
                                  fontSize: "12px",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  backgroundColor:
                                    getSentimentColor(user.sentiment) + "20",
                                  color: getSentimentColor(user.sentiment),
                                }}
                              >
                                {user.sentiment}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Topic Trends */}
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                      }}
                    >
                      <h4
                        style={{
                          marginTop: 0,
                          marginBottom: "16px",
                          color: "#e2e8f0",
                        }}
                      >
                        Trending Topics
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {topicTrends.slice(0, 5).map((trend) => (
                          <div
                            key={trend.topic}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px",
                              backgroundColor: "#374151",
                              borderRadius: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span>{getTrendIcon(trend.trend)}</span>
                              <span
                                style={{
                                  fontWeight: "500",
                                  textTransform: "capitalize",
                                  color: "#e2e8f0",
                                }}
                              >
                                {trend.topic}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "12px",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{ fontSize: "14px", color: "#cbd5e0" }}
                              >
                                {trend.mentions} mentions
                              </span>
                              <span
                                style={{
                                  fontSize: "12px",
                                  color:
                                    trend.percentChange > 0
                                      ? "#10B981"
                                      : trend.percentChange < 0
                                        ? "#EF4444"
                                        : "#6B7280",
                                }}
                              >
                                {trend.percentChange > 0 ? "+" : ""}
                                {trend.percentChange.toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Sentiment Breakdown */}
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "20px",
                      borderRadius: "8px",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        color: "#e2e8f0",
                      }}
                    >
                      Sentiment Analysis
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "repeat(2, 1fr)"
                          : "repeat(4, 1fr)",
                        gap: "16px",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#10B981",
                          }}
                        >
                          {metrics.sentimentBreakdown.positive}
                        </div>
                        <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                          Positive
                        </div>
                        <div
                          style={{
                            marginTop: "8px",
                            height: "4px",
                            backgroundColor: "#10B981",
                            borderRadius: "2px",
                          }}
                        />
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#6B7280",
                          }}
                        >
                          {metrics.sentimentBreakdown.neutral}
                        </div>
                        <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                          Neutral
                        </div>
                        <div
                          style={{
                            marginTop: "8px",
                            height: "4px",
                            backgroundColor: "#6B7280",
                            borderRadius: "2px",
                          }}
                        />
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#EF4444",
                          }}
                        >
                          {metrics.sentimentBreakdown.negative}
                        </div>
                        <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                          Negative
                        </div>
                        <div
                          style={{
                            marginTop: "8px",
                            height: "4px",
                            backgroundColor: "#EF4444",
                            borderRadius: "2px",
                          }}
                        />
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#3B82F6",
                          }}
                        >
                          {metrics.sentimentBreakdown.questions}
                        </div>
                        <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                          Questions
                        </div>
                        <div
                          style={{
                            marginTop: "8px",
                            height: "4px",
                            backgroundColor: "#3B82F6",
                            borderRadius: "2px",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Topic Distribution */}
                  {Object.keys(metrics.topicDistribution).length > 0 && (
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                      }}
                    >
                      <h4
                        style={{
                          marginTop: 0,
                          marginBottom: "16px",
                          color: "#e2e8f0",
                        }}
                      >
                        Topic Distribution
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        {Object.entries(metrics.topicDistribution).map(
                          ([topic, count]) => (
                            <span
                              key={topic}
                              style={{
                                padding: "6px 16px",
                                backgroundColor: "#E0E7FF",
                                color: "#4338CA",
                                borderRadius: "20px",
                                fontSize: "14px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span style={{ textTransform: "capitalize" }}>
                                {topic}
                              </span>
                              <span style={{ fontWeight: "bold" }}>
                                {count}
                              </span>
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: isMobile ? "40px 20px" : "60px",
                    borderRadius: "8px",
                    textAlign: "center",
                    marginTop: "20px",
                    border: "1px solid #4a5568",
                    boxSizing: "border-box",
                    width: "100%",
                  }}
                >
                  <div style={{ fontSize: "24px", marginBottom: "16px" }}>
                    <Icon name="bar-chart-3" size={40} color="#9146FF" />
                  </div>
                  <h3
                    style={{
                      color: "#e2e8f0",
                      marginTop: 0,
                      marginBottom: "12px",
                      fontSize: "20px",
                    }}
                  >
                    No Analytics Data Available
                  </h3>
                  <p
                    style={{
                      color: "#a0aec0",
                      marginBottom: "20px",
                      fontSize: "16px",
                    }}
                  >
                    HAL needs to be active in your channel and process messages
                    to generate analytics.
                  </p>
                  <div
                    style={{
                      color: "#cbd5e0",
                      fontSize: "14px",
                      lineHeight: "1.8",
                    }}
                  >
                    <p style={{ margin: "0 0 8px 0" }}>
                      To start collecting data:
                    </p>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        margin: "0 auto",
                        textAlign: "left",
                        display: "inline-block",
                      }}
                    >
                      <li>Add HAL to your channel</li>
                      <li>Make sure HAL is modded in your channel</li>
                      <li>Start streaming and chatting</li>
                      <li>Check back after some activity</li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Stream Analytics Tab */
            <>
              {loading ? (
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: isMobile ? "40px 20px" : "60px",
                    borderRadius: "8px",
                    textAlign: "center",
                    marginTop: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "400px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "48px",
                      marginBottom: "16px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  >
                    <Icon name="film" size={40} color="#9146FF" />
                  </div>
                  <h3
                    style={{
                      color: "#e2e8f0",
                      marginTop: 0,
                      marginBottom: "12px",
                      fontSize: "20px",
                    }}
                  >
                    Loading Stream Analytics...
                  </h3>
                  <p
                    style={{
                      color: "#a0aec0",
                      fontSize: "16px",
                    }}
                  >
                    Fetching your stream data
                  </p>
                </div>
              ) : streamAnalytics ? (
                <div style={{ display: "grid", gap: "20px" }}>
                  {/* Key Stream Metrics */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "repeat(2, 1fr)"
                        : "repeat(4, 1fr)",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#9146FF",
                        }}
                      >
                        {streamAnalytics.totalStreams}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Total Streams
                      </div>
                    </div>
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#9146FF",
                        }}
                      >
                        {formatDuration(streamAnalytics.avgStreamDuration)}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Avg Duration
                      </div>
                    </div>
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#9146FF",
                        }}
                      >
                        {streamAnalytics.overallPeakViewers}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Peak Viewers
                      </div>
                    </div>
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#9146FF",
                        }}
                      >
                        {streamAnalytics.avgPeakViewers}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Avg Peak
                      </div>
                    </div>
                  </div>

                  {/* Viewer Count Chart */}
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "20px",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                      width: "100%",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        color: "#e2e8f0",
                      }}
                    >
                      {streamAnalytics.timeRange === "24h"
                        ? "Viewer Count by Hour (Max)"
                        : "Daily Peak Viewers"}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        gap: "2px",
                        alignItems: "flex-end",
                        marginBottom: "8px",
                      }}
                    >
                      {getViewerChartBars()}
                    </div>
                    <p
                      style={{
                        textAlign: "center",
                        marginTop: "12px",
                        fontSize: "14px",
                        color: "#cbd5e0",
                      }}
                    >
                      {streamAnalytics.timeRange === "24h"
                        ? "Maximum viewers per hour over the last 24 hours"
                        : `Daily peak viewers over the last ${streamAnalytics.timeRange}`}
                    </p>
                  </div>

                  {/* Stream List */}
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "20px",
                      borderRadius: "8px",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        color: "#e2e8f0",
                      }}
                    >
                      Recent Streams
                    </h4>
                    {streamAnalytics.streams.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        {streamAnalytics.streams.slice(0, 10).map((stream) => (
                          <div
                            key={stream.id}
                            style={{
                              display: "flex",
                              flexDirection: isMobile ? "column" : "row",
                              justifyContent: "space-between",
                              padding: "12px",
                              backgroundColor: "#374151",
                              borderRadius: "6px",
                              gap: "12px",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: "500",
                                  color: "#e2e8f0",
                                  marginBottom: "4px",
                                }}
                              >
                                {stream.title || "Untitled Stream"}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#a0aec0",
                                  display: "flex",
                                  gap: "12px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span>
                                  <Icon
                                    name="gamepad-2"
                                    size={13}
                                    style={{
                                      verticalAlign: "-2px",
                                      marginRight: "4px",
                                    }}
                                  />
                                  {stream.category}
                                </span>
                                <span>
                                  <Icon
                                    name="calendar"
                                    size={13}
                                    style={{
                                      verticalAlign: "-2px",
                                      marginRight: "4px",
                                    }}
                                  />{" "}
                                  {new Date(
                                    stream.startedAt,
                                  ).toLocaleDateString()}{" "}
                                  at{" "}
                                  {new Date(
                                    stream.startedAt,
                                  ).toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "2-digit",
                                  })}
                                </span>
                                <span>
                                  <Icon
                                    name="clock"
                                    size={13}
                                    style={{
                                      verticalAlign: "-2px",
                                      marginRight: "4px",
                                    }}
                                  />
                                  {formatDuration(stream.duration)}
                                </span>
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: "20px",
                                alignItems: "center",
                              }}
                            >
                              <div style={{ textAlign: "center" }}>
                                <div
                                  style={{
                                    fontSize: "20px",
                                    fontWeight: "bold",
                                    color: "#9146FF",
                                  }}
                                >
                                  {stream.peakViewers}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#cbd5e0",
                                  }}
                                >
                                  Peak
                                </div>
                              </div>
                              <div style={{ textAlign: "center" }}>
                                <div
                                  style={{
                                    fontSize: "20px",
                                    fontWeight: "bold",
                                    color: "#E0E7FF",
                                  }}
                                >
                                  {stream.avgViewers}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#cbd5e0",
                                  }}
                                >
                                  Avg
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: "#a0aec0", textAlign: "center" }}>
                        No streams found in this time period
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: isMobile ? "40px 20px" : "60px",
                    borderRadius: "8px",
                    textAlign: "center",
                    marginTop: "20px",
                    border: "1px solid #4a5568",
                    boxSizing: "border-box",
                    width: "100%",
                  }}
                >
                  <div style={{ fontSize: "24px", marginBottom: "16px" }}>
                    <Icon name="film" size={40} color="#9146FF" />
                  </div>
                  <h3
                    style={{
                      color: "#e2e8f0",
                      marginTop: 0,
                      marginBottom: "12px",
                      fontSize: "20px",
                    }}
                  >
                    No Stream Data Available
                  </h3>
                  <p
                    style={{
                      color: "#a0aec0",
                      marginBottom: "20px",
                      fontSize: "16px",
                    }}
                  >
                    Start streaming to see your viewer analytics and stream
                    performance metrics.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Analytics Tips Panel */}
        <div
          style={{
            width: isMobile ? "100%" : "350px",
            maxWidth: isMobile ? "800px" : "350px",
            backgroundColor: "rgba(45, 55, 72, 0.95)",
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            borderRadius: "8px",
            padding: "20px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
            height: "fit-content",
            position: isMobile ? "static" : "sticky",
            top: "80px",
            order: isMobile ? 2 : 0,
            boxSizing: "border-box",
          }}
        >
          <h3
            style={{
              color: "#e2e8f0",
              marginTop: 0,
              marginBottom: "20px",
              fontSize: "18px",
            }}
          >
            <GuideIcon name="bar-chart-3" /> Analytics Guide
          </h3>

          {activeTab === "chat" ? (
            <>
              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="clock" /> Time Ranges
                </h4>
                <p
                  style={{
                    color: "#cbd5e0",
                    fontSize: "13px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  Toggle between 24 hours, 7 days, or 30 days to analyze
                  different time periods.
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="users-round" /> Active Users
                </h4>
                <p
                  style={{
                    color: "#cbd5e0",
                    fontSize: "13px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  Shows your most engaged viewers with message counts and their
                  general sentiment.
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="activity" /> Activity Patterns
                </h4>
                <p
                  style={{
                    color: "#cbd5e0",
                    fontSize: "13px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  The hourly chart reveals when your chat is most active. Purple
                  bar = peak hour.
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="target" /> Trending Topics
                </h4>
                <p
                  style={{
                    color: "#cbd5e0",
                    fontSize: "13px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  Track what viewers are talking about. Icons show if topics are
                  rising{" "}
                  <Icon
                    name="trending-up"
                    size={13}
                    style={{ verticalAlign: "-2px" }}
                  />{" "}
                  or declining{" "}
                  <Icon
                    name="trending-down"
                    size={13}
                    style={{ verticalAlign: "-2px" }}
                  />
                  .
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="message-square" /> Sentiment Analysis
                </h4>
                <p
                  style={{
                    color: "#cbd5e0",
                    fontSize: "13px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  Understand chat mood: positive, neutral, negative, or
                  questions.
                </p>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="film" /> What&apos;s Coming
                </h4>
                <p
                  style={{
                    color: "#cbd5e0",
                    fontSize: "13px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  Stream Analytics will help you understand and improve your
                  streaming performance with detailed metrics.
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="bar-chart-3" /> Key Metrics
                </h4>
                <ul
                  style={{
                    color: "#cbd5e0",
                    fontSize: "12px",
                    margin: 0,
                    paddingLeft: "18px",
                  }}
                >
                  <li>Live viewer counts</li>
                  <li>Stream duration tracking</li>
                  <li>Follower conversion rates</li>
                  <li>Chat-to-viewer ratio</li>
                </ul>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="rocket" /> Growth Insights
                </h4>
                <p
                  style={{
                    color: "#cbd5e0",
                    fontSize: "13px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  Track raid impact, host effectiveness, and discover what
                  drives viewer growth.
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    color: "#9146FF",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <GuideIcon name="target" /> Performance Goals
                </h4>
                <p
                  style={{
                    color: "#cbd5e0",
                    fontSize: "13px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  Set and track streaming goals with historical comparisons and
                  trend analysis.
                </p>
              </div>
            </>
          )}

          <div
            style={{
              backgroundColor: "#1a202c",
              padding: "12px",
              borderRadius: "6px",
              marginTop: "15px",
              border: "1px solid #4a5568",
            }}
          >
            <p
              style={{
                color: "#9146FF",
                fontSize: "12px",
                margin: "0 0 6px 0",
                fontWeight: "bold",
              }}
            >
              <GuideIcon name="lightbulb" /> Pro Tips:
            </p>
            <ul
              style={{
                color: "#a0aec0",
                fontSize: "11px",
                margin: 0,
                paddingLeft: "16px",
              }}
            >
              <li>Peak hours = best time to stream</li>
              <li>Track sentiment to gauge audience mood</li>
              <li>Use trending topics for content ideas</li>
              <li>Engage with your most active users</li>
              <li>Monitor conversation threads for engagement</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <Routes>
      <Route path="chat/last24hours" element={<AnalyticsContent />} />
      <Route path="chat/last7days" element={<AnalyticsContent />} />
      <Route path="chat/last30days" element={<AnalyticsContent />} />
      <Route path="stream/last24hours" element={<AnalyticsContent />} />
      <Route path="stream/last7days" element={<AnalyticsContent />} />
      <Route path="stream/last30days" element={<AnalyticsContent />} />
      <Route path="chat" element={<Navigate to="chat/last24hours" replace />} />
      <Route
        path="stream"
        element={<Navigate to="stream/last24hours" replace />}
      />
      <Route path="/" element={<Navigate to="chat/last24hours" replace />} />
    </Routes>
  );
};

export default AnalyticsDashboard;
